import { UseFormReturn } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pencil, Check, X } from "lucide-react";
import { TenantFormData } from "@/lib/schemas/tenant";

interface Step4ReviewProps {
  form: UseFormReturn<TenantFormData>;
  onEditStep: (step: number) => void;
}

const planNames: Record<string, string> = {
  starter: "Starter",
  pro: "Pro",
  enterprise: "Enterprise",
};

export function Step4Review({ form, onEditStep }: Step4ReviewProps) {
  const values = form.getValues();

  const Section = ({
    title,
    step,
    children,
  }: {
    title: string;
    step: number;
    children: React.ReactNode;
  }) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
          {title}
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEditStep(step)}
          className="h-7 px-2 text-xs"
        >
          <Pencil className="h-3 w-3 mr-1" />
          Edit
        </Button>
      </div>
      {children}
    </div>
  );

  const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex justify-between py-2 border-b border-border/50 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value || "-"}</span>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Review & Create</h2>
        <p className="text-sm text-muted-foreground">
          Please review the customer details before creating
        </p>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            {values.name}
            <Badge variant="outline" className="font-normal">
              {planNames[values.plan_id] || values.plan_id}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Section title="Basic Information" step={1}>
            <Field label="Business Name" value={values.name} />
            <Field
              label="URL Slug"
              value={
                <code className="text-sm bg-muted px-1.5 py-0.5 rounded">
                  {values.slug}
                </code>
              }
            />
            <Field label="Custom Domain" value={values.domain} />
          </Section>

          <Section title="Contact & Billing" step={2}>
            <Field label="Billing Email" value={values.billing_email} />
            <Field
              label="Bill Through Agency"
              value={
                values.billing_via_agency ? (
                  <span className="flex items-center gap-1 text-green-500">
                    <Check className="h-4 w-4" /> Yes
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <X className="h-4 w-4" /> No
                  </span>
                )
              }
            />
          </Section>

          <Section title="Plan & Limits" step={3}>
            <Field label="Plan" value={planNames[values.plan_id] || values.plan_id} />
            <Field
              label="Max Users"
              value={values.max_users ?? "Plan default"}
            />
            <Field
              label="Max Phone Numbers"
              value={values.max_phone_numbers ?? "Plan default"}
            />
            <Field
              label="Max SMS per Day"
              value={values.max_sms_per_day ?? "Plan default"}
            />
          </Section>
        </CardContent>
      </Card>
    </div>
  );
}
